/** @shell standard @header bar */
import React, { useMemo, useCallback, useState, useEffect, useLayoutEffect, useRef, memo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Image,
  RefreshControl,
  Platform,
  useWindowDimensions,
  NativeSyntheticEvent,
  NativeScrollEvent,
  Pressable,
} from 'react-native';
import { useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { AppLinearGradient as LinearGradient } from '../components/ui/AppLinearGradient';
import { useAuthStore } from '../shared/auth/store';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useAuthSheets } from '../shared/auth/authSheetContext';
import { appTheme, useThemeColors } from '../shared/theme';
import { hexToRgba } from '../shared/theme/colorUtils';
import { useTranslation, getApiText } from '../shared/i18n';
import { trailingRowDisclosureChevron, horizontalLtrDirectionStyle } from '../shared/i18n/rtl';
import {
  useSubscriptionPlans,
  processSubscriptionData,
  setSelectedSubscription,
  shouldOfferFreeDaysModal,
  planImageFromCatalogPlanLike,
  type PlanItem,
} from '../data/domains/subscription';
import {
  collectPortionOptionsFromRegularPlans,
  formatPersonCountLabel,
  getCatalogPlanPortion,
  catalogPlanMatchesSelectedPortion,
  portionsEqual,
} from '../data/domains/subscription/catalog/subscriptionPortion';
import { resolvePlanOfferPricing, getEffectivePlanUnitPrice } from '../data/domains/subscription/catalog/subscriptionPlanPrice';
import { getSubscriptionListStyle, usePortionTextMode } from '../data/domains/config';
import { setGuestExplorePlan } from '../data/domains/guest';
import { Clickable } from '../components/common/Clickable';
import { SvgIcon } from '../components/common/SvgIcon';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState } from '../components/common/ErrorState';
import { EmptyState } from '../components/common/EmptyState';
import { formatCurrency } from '../shared/utils/currencyUtils';
import { resolveWebLayoutWidth } from '../shared/platform/webLayout';
import { resolveAttachmentUri, resolveMediaUri } from '../shared/utils/resolveMediaUri';
import { ScreenHeader, useHeaderTopPadding } from '../components/common/ScreenHeader';
import { BottomSheetPopup } from '../components/common/BottomSheetPopup';
import { StackScreenShell } from '../shared/theme/studio/stackScreenShell';
import {
  SubscriptionPlanEdgeImage,
} from '../dashboard/components/variants/set2/SubscriptionPlanEdgeImage';
import type { AuthStackParamList } from '../navigation/types';
import {
  getSubscriptionScrollPaddingTop,
  SUBSCRIPTION_SCREEN_BOTTOM_BAR_CLEARANCE,
} from './subscriptionScreenLayout';
import {
  buildPremiumCategories,
  buildDurationsForCategory,
  buildPlanItemsForCategoryAndDuration,
  formatSubscriptionFooterTotal,
  getCategoryBannerSize,
  CATEGORY_STRIP_GAP,
  type PremiumCategoryItem,
} from './lib/subscriptionPremiumListHelpers';
import {
  createSubscriptionPremiumListStyles,
  premiumCategoryInnerRadius,
  type SubscriptionPremiumListStyles,
} from './components/subscriptionPremiumListStyles';

type SubscriptionListRoute = RouteProp<AuthStackParamList, 'SubscriptionList'>;

const CategoryStripItem = memo(function CategoryStripItem({
  cat,
  is_active,
  on_press,
  card_width,
  card_height,
  margin_end,
  image_uri,
  styles,
  colors: tc,
}: {
  cat: PremiumCategoryItem;
  is_active: boolean;
  on_press: () => void;
  card_width: number;
  card_height: number;
  margin_end: number;
  image_uri: string | null;
  styles: SubscriptionPremiumListStyles;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const { t } = useTranslation();
  const [image_error, set_image_error] = useState(false);
  const title = getApiText(cat as unknown as Record<string, unknown>, 'title', 'arabic_title') || cat.title;
  const show_image = Boolean(image_uri) && !image_error;
  const inner_r = premiumCategoryInnerRadius(is_active);

  useEffect(() => {
    set_image_error(false);
  }, [image_uri]);

  const a11y_label = is_active ? `${title} (${t('account.selected')})` : title;

  return (
    <Clickable
      onPress={on_press}
      pressAnimated={false}
      accessibilityRole="button"
      accessibilityLabel={a11y_label}
      contentStyle={styles.clickableFill}
      style={[
        styles.categoryCard,
        {
          width: card_width,
          height: card_height,
          marginEnd: margin_end,
        },
        is_active && styles.categoryCardActive,
      ]}>
      <View style={[styles.categoryCardMediaMask, { borderRadius: inner_r }]}>
        <View style={[styles.categoryCardClip, { borderRadius: inner_r }]}>
          <View style={[styles.categoryCardImageArea, { borderTopLeftRadius: inner_r, borderTopRightRadius: inner_r }]}>
            {show_image ? (
              <Image
                source={{ uri: image_uri! }}
                style={styles.categoryCardImage}
                resizeMode="cover"
                fadeDuration={0}
                onError={() => set_image_error(true)}
              />
            ) : (
              <View style={[styles.categoryCardFallback, { backgroundColor: tc.primaryUltra }]}>
                <SvgIcon name="meal" size={36} color={tc.primary} />
              </View>
            )}
          </View>
          <View style={styles.categoryCardTitleBar}>
            <Text style={styles.categoryCardTitle} numberOfLines={2} ellipsizeMode="tail">
              {title}
            </Text>
          </View>
        </View>
      </View>
    </Clickable>
  );
});

export const SubscriptionListPremiumScreen: React.FC = () => {
  const navigation = useAuthNavigation();
  const on_back_exit = useExitOrGoBack();
  const route = useRoute<SubscriptionListRoute>();
  const guest_explore = route.params?.guestExplore === true;
  const insets = useSafeAreaInsets();
  const c = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createSubscriptionPremiumListStyles(c), [c]);
  const { width: window_width } = useWindowDimensions();
  const layout_width = resolveWebLayoutWidth(window_width);
  const header_top = useHeaderTopPadding();
  const { t } = useTranslation();
  const { openRegister } = useAuthSheets();
  const user = useAuthStore(s => s.user);
  const is_logged_in = !!user;
  const use_grid_layout = getSubscriptionListStyle() === 'grid';
  const portion_text_mode = usePortionTextMode();
  const category_strip_ref = useRef<ScrollView>(null);

  const { width: category_banner_w, height: category_banner_h } = useMemo(() => getCategoryBannerSize(layout_width), [layout_width]);
  const category_stride = category_banner_w + CATEGORY_STRIP_GAP;
  const category_side_pad = Math.max(0, (layout_width - category_banner_w) / 2);

  const { data: plans, isLoading, error, refetch } = useSubscriptionPlans();
  const [refreshing, set_refreshing] = useState(false);
  const [selected_category_id, set_selected_category_id] = useState<string | number | null>(null);
  const [selected_portion, set_selected_portion] = useState(1);
  const [selected_duration, set_selected_duration] = useState<number | null>(null);
  const [selected_plan, set_selected_plan] = useState<PlanItem | null>(null);
  const [show_free_days_modal, set_show_free_days_modal] = useState(false);
  const [selected_plan_with_free_days, set_selected_plan_with_free_days] = useState<any>(null);
  const [include_free_days, set_include_free_days] = useState(false);

  const { regular_plans } = useMemo(() => processSubscriptionData(Array.isArray(plans) ? plans : []), [plans]);
  const categories = useMemo(() => buildPremiumCategories(regular_plans), [regular_plans]);

  const category_image_uris = useMemo(() => {
    const out = new Map<string | number, string | null>();
    for (const cat of categories) {
      out.set(cat.id, resolveAttachmentUri(cat.attachment));
    }
    return out;
  }, [categories]);

  useEffect(() => {
    for (const uri of category_image_uris.values()) {
      if (uri) {
        Image.prefetch(uri).catch(() => {});
      }
    }
  }, [category_image_uris]);

  const member_portion_options = useMemo(() => collectPortionOptionsFromRegularPlans(regular_plans), [regular_plans]);

  useEffect(() => {
    if (member_portion_options.length === 0) return;
    if (!member_portion_options.some(p => portionsEqual(p, selected_portion))) {
      set_selected_portion(member_portion_options[0]);
    }
  }, [member_portion_options, selected_portion]);

  const selected_category = useMemo(
    () => categories.find(cat => String(cat.id) === String(selected_category_id)) ?? categories[0] ?? null,
    [categories, selected_category_id],
  );

  useEffect(() => {
    if (categories.length > 0 && selected_category_id == null) {
      set_selected_category_id(categories[0].id);
    }
  }, [categories, selected_category_id]);

  const durations = useMemo(() => buildDurationsForCategory(selected_category, selected_portion), [selected_category, selected_portion]);
  const effective_duration = selected_duration ?? durations[0] ?? null;

  useEffect(() => {
    if (durations.length > 0 && (selected_duration == null || !durations.includes(selected_duration))) {
      set_selected_duration(durations[0]);
    }
  }, [durations, selected_duration]);

  const plan_items = useMemo(
    () => buildPlanItemsForCategoryAndDuration(selected_category, effective_duration ?? 0, selected_portion),
    [selected_category, effective_duration, selected_portion],
  );

  useEffect(() => {
    for (const item of plan_items) {
      const uri =
        planImageFromCatalogPlanLike(item.plan) ??
        resolveMediaUri(item.categoryImage) ??
        resolveMediaUri(item.plan.thumbnail ?? item.plan.attachment);
      if (uri) {
        Image.prefetch(uri).catch(() => {});
      }
    }
  }, [plan_items]);

  useEffect(() => {
    if (!selected_plan) return;
    const plan = selected_plan.plan as Record<string, unknown>;
    if (!catalogPlanMatchesSelectedPortion(plan, selected_portion)) {
      set_selected_plan(null);
    }
  }, [selected_portion, selected_plan]);

  const scroll_category_to_index = useCallback(
    (index: number, animated: boolean) => {
      const x = Math.round(index * category_stride);
      category_strip_ref.current?.scrollTo({ x, animated });
    },
    [category_stride],
  );

  const on_category_momentum_end = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const x = e.nativeEvent.contentOffset.x;
      let idx = Math.round(x / category_stride);
      if (idx < 0) idx = 0;
      if (idx >= categories.length) idx = Math.max(0, categories.length - 1);
      const cat = categories[idx];
      if (!cat) return;
      if (String(cat.id) === String(selected_category_id)) return;
      set_selected_category_id(cat.id);
      set_selected_duration(null);
      set_selected_plan(null);
    },
    [categories, category_stride, selected_category_id],
  );

  const selected_category_id_ref = useRef(selected_category_id);
  selected_category_id_ref.current = selected_category_id;

  useLayoutEffect(() => {
    if (categories.length === 0) return;
    const idx = Math.max(
      0,
      categories.findIndex(cat => String(cat.id) === String(selected_category_id_ref.current)),
    );
    scroll_category_to_index(idx, false);
  }, [categories, category_stride, scroll_category_to_index]);

  const on_refresh = useCallback(async () => {
    set_refreshing(true);
    await refetch();
    set_refreshing(false);
  }, [refetch]);

  const handle_select_plan = useCallback(
    (item: PlanItem) => {
      const plan = item.plan;
      const plan_portion = getCatalogPlanPortion(plan as Record<string, unknown>);
      const portion_for_api = plan_portion ?? selected_portion;
      const plan_for_checkout = {
        ...plan,
        days: item.days,
        id: plan.id ?? plan.subscription_id,
        subscription_id: plan.subscription_id ?? plan.id,
        portion: portion_for_api,
        no_meals_on_days: plan.no_meals_on_days ?? [],
        max_day_off: plan.max_day_off ?? 0,
        celebrity_id: plan.celebrity_id ?? 0,
        number_of_meals: plan.number_of_meals ?? plan.total_meals ?? 0,
        number_of_snacks: plan.number_of_snacks ?? plan.total_snacks ?? 0,
        customized_meals: plan.customized_meals ?? plan.is_customized ?? false,
        isCustomAllowed: plan.isCustomAllowed ?? plan.is_customized ?? false,
      };

      const go_to_checkout = async (free_days_selected: number) => {
        const payload = { ...plan_for_checkout, free_days_selected };
        if (guest_explore && !is_logged_in) {
          await setGuestExplorePlan(payload as Record<string, unknown>);
          navigation.navigate('GuestMenuPreview', { plan: payload });
          return;
        }
        if (!is_logged_in) {
          await setSelectedSubscription(payload);
          openRegister({ subData: payload });
        } else {
          navigation.navigate('Checkout', { plan: payload, free_days_selected });
        }
      };

      if (shouldOfferFreeDaysModal(plan)) {
        set_selected_plan_with_free_days(plan_for_checkout);
        set_include_free_days(false);
        set_show_free_days_modal(true);
        return;
      }
      go_to_checkout(0);
    },
    [navigation, is_logged_in, selected_portion, guest_explore, openRegister],
  );

  const handle_confirm_free_days = useCallback(async () => {
    if (!selected_plan_with_free_days) {
      return;
    }
    const free_add = include_free_days ? Number(selected_plan_with_free_days.free_days ?? 0) : 0;
    const plan_with_free_days = {
      ...selected_plan_with_free_days,
      free_days_selected: free_add,
    };
    set_show_free_days_modal(false);
    set_selected_plan_with_free_days(null);
    if (guest_explore && !is_logged_in) {
      await setGuestExplorePlan(plan_with_free_days as Record<string, unknown>);
      navigation.navigate('GuestMenuPreview', { plan: plan_with_free_days });
      return;
    }
    if (!is_logged_in) {
      await setSelectedSubscription(plan_with_free_days);
      openRegister({ subData: plan_with_free_days });
    } else {
      navigation.navigate('Checkout', { plan: plan_with_free_days, free_days_selected: free_add });
    }
  }, [selected_plan_with_free_days, include_free_days, navigation, is_logged_in, guest_explore, openRegister]);

  const handle_proceed = useCallback(() => {
    if (!selected_plan) return;
    handle_select_plan(selected_plan);
  }, [selected_plan, handle_select_plan]);

  const header_refresh = (
    <Clickable onPress={() => refetch?.()} style={styles.headerRefreshWrap} accessibilityLabel={t('common.refresh')} accessibilityRole="button">
      <View style={styles.headerRefreshInner}>
        <SvgIcon name="refresh" size={18} color={c.text} />
      </View>
    </Clickable>
  );

  const open_guest_sample_menu = useCallback(async () => {
    const sample_plan: Record<string, unknown> = {
      id: 0,
      subscription_id: 0,
      title: t('subscriptions.chooseYourPlan'),
      number_of_meals: 3,
      number_of_snacks: 2,
      days: 1,
      _guest_sample: true,
    };
    await setGuestExplorePlan(sample_plan);
    navigation.navigate('GuestMenuPreview', { plan: sample_plan });
  }, [navigation, t]);

  const screen_title = t('subscriptions.chooseYourPlan') || t('subscriptions.plans');

  if (isLoading) {
    return (
      <StackScreenShell colors={c}>
        <View style={styles.container}>
          <ScreenHeader title={screen_title} onBack={on_back_exit} rightAction={header_refresh} />
          <View style={[styles.stateBelowHeader, { paddingTop: header_top }]}>
            <LoadingState message={t('subscriptions.loadingPlans')} />
          </View>
        </View>
      </StackScreenShell>
    );
  }

  if (error) {
    return (
      <StackScreenShell colors={c}>
        <View style={styles.container}>
          <ScreenHeader title={screen_title} onBack={on_back_exit} rightAction={header_refresh} />
          <View style={[styles.stateBelowHeader, { paddingTop: header_top }]}>
            <ErrorState
              title={t('subscriptions.failedToLoadPlans')}
              message={t('subscriptions.tryAgain')}
              onRetry={refetch}
              error={error}
              issue_context={{
                page: t('account.browsePlans'),
                issue: 'Failed to load subscription plans',
                kind: 'api',
                api_endpoint: 'subscriptions',
                http_method: 'GET',
              }}
            />
          </View>
        </View>
      </StackScreenShell>
    );
  }

  if (regular_plans.length === 0) {
    return (
      <StackScreenShell colors={c}>
        <View style={styles.container}>
          <ScreenHeader title={screen_title} onBack={on_back_exit} rightAction={header_refresh} />
          <View style={[styles.stateBelowHeader, { paddingTop: header_top }]}>
            {guest_explore && !is_logged_in ? (
              <View style={{ width: '100%' }}>
                <EmptyState icon="restaurant-outline" title={t('subscriptions.noPlansAvailable')} message={t('guest.noPlansGuestHint') || t('subscriptions.noPlansMessage')} />
                <View style={{ paddingHorizontal: 24, marginTop: 8 }}>
                  <Clickable onPress={open_guest_sample_menu} accessibilityLabel={t('guest.guestBrowseSample')} accessibilityRole="button" style={styles.guestSampleBtn}>
                    <Text style={styles.guestSampleBtnText}>{t('guest.guestBrowseSample')}</Text>
                  </Clickable>
                </View>
              </View>
            ) : (
              <EmptyState icon="restaurant-outline" title={t('subscriptions.noPlansAvailable')} message={t('subscriptions.noPlansMessage')} />
            )}
          </View>
        </View>
      </StackScreenShell>
    );
  }

  const primary_gradient = [c.primary ?? '#35858b', c.primaryDark ?? c.primary ?? '#35858b'];
  const total_label = selected_plan
    ? formatCurrency(getEffectivePlanUnitPrice(selected_plan.plan as { offer_cost?: unknown; cost?: unknown }))
    : '—';
  const plan_summary_label = selected_plan
    ? `${selected_plan.days} ${t('subscriptions.daysLabel')} · ${formatPersonCountLabel(getCatalogPlanPortion(selected_plan.plan as Record<string, unknown>) ?? 1, t, portion_text_mode)} · ${getApiText(selected_plan.plan as Record<string, unknown>, 'title', 'arabic_title') || selected_plan.plan.title || selected_plan.categoryTitle}`
    : t('subscriptions.choosePlan') || 'Choose a plan';

  const scroll_padding_top = getSubscriptionScrollPaddingTop(header_top);

  const render_plan_card = (item: PlanItem) => {
    const is_selected = selected_plan?.key === item.key;
    const { displayPrice: price, strikethroughPrice: list_price } = resolvePlanOfferPricing(item.plan as { offer_cost?: unknown; cost?: unknown });
    const has_offer = list_price != null;
    const plan_obj = item.plan as Record<string, unknown>;
    const title_str =
      getApiText(plan_obj, 'title', 'arabic_title') ||
      getApiText(plan_obj, 'name', 'arabic_title') ||
      (typeof plan_obj.name === 'string' ? plan_obj.name : '') ||
      (typeof plan_obj.label === 'string' ? plan_obj.label : '') ||
      (typeof item.plan.title === 'string' ? item.plan.title : '') ||
      (typeof item.plan.name === 'string' ? item.plan.name : '') ||
      item.categoryTitle;
    const image_uri =
      planImageFromCatalogPlanLike(item.plan) ??
      resolveMediaUri(item.categoryImage) ??
      resolveMediaUri(item.plan.thumbnail ?? item.plan.attachment);
    const plan_portion_display = getCatalogPlanPortion(item.plan as Record<string, unknown>) ?? 1;
    const meals_count = Number(item.plan.number_of_meals ?? item.plan.total_meals ?? (plan_obj.number_of_meal as number | undefined) ?? 0);
    const snacks_count = Number(item.plan.number_of_snacks ?? item.plan.total_snacks ?? 0);
    const show_meals_snacks = (Number.isFinite(meals_count) && meals_count > 0) || (Number.isFinite(snacks_count) && snacks_count > 0);

    return (
      <Clickable
        key={item.key}
        onPress={() => set_selected_plan(item)}
        pressAnimated={false}
        contentStyle={styles.clickableFillWidth}
        accessibilityRole="button"
        accessibilityLabel={is_selected ? `${title_str} (${t('account.selected')})` : title_str}
        style={[styles.planCardOuter, use_grid_layout && styles.planCardOuterGrid]}>
        <View style={[styles.planCardRoot, is_selected && styles.planCardRootSelected]}>
          <SubscriptionPlanEdgeImage
            uri={image_uri}
            border_radius={appTheme.card.radius}
            colors={c}
            fallback={<SvgIcon name="meal" size={36} color={c.primary} />}
          />
          <View style={styles.planCardContent}>
            <Text style={styles.planCardTitle} numberOfLines={3} ellipsizeMode="tail">
              {title_str}
            </Text>
            <Text style={styles.planCardMembersLine} numberOfLines={1}>
              {formatPersonCountLabel(plan_portion_display, t, portion_text_mode)}
            </Text>
            {show_meals_snacks ? (
              <Text style={styles.planCardMealsLine} numberOfLines={2}>
                {meals_count} {t('dashboard.mealsLabel')}
                {', '}
                {snacks_count} {t('dashboard.snacksLabel')}
              </Text>
            ) : null}
            <View style={styles.planCardPriceRow}>
              {has_offer ? (
                <Text style={[styles.planCardListPrice, { color: c.danger }]} numberOfLines={1}>
                  {formatCurrency(Number(list_price))}
                </Text>
              ) : null}
              <View style={styles.planCardPricePillShell}>
                <LinearGradient colors={primary_gradient as [string, string]} style={StyleSheet.absoluteFill} />
                <View style={styles.planCardPricePillInset}>
                  <Text style={styles.planCardPriceText}>{formatCurrency(Number(price))}</Text>
                </View>
              </View>
            </View>
          </View>

          {is_selected ? (
            <View
              style={[styles.planCardCheckBadge, { right: 10, left: undefined, backgroundColor: c.primary }]}
              pointerEvents="none"
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants">
              <SvgIcon name="check" size={16} color={c.onPrimary ?? c.surface} />
            </View>
          ) : null}
        </View>
      </Clickable>
    );
  };

  return (
    <StackScreenShell colors={c}>
      <View style={styles.container}>
        <ScreenHeader title={screen_title} onBack={on_back_exit} rightAction={header_refresh} />
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={[
            styles.scrollContent,
            {
              paddingTop: scroll_padding_top,
              paddingBottom: (insets.bottom || 0) + SUBSCRIPTION_SCREEN_BOTTOM_BAR_CLEARANCE,
            },
          ]}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={on_refresh} tintColor={c.primary} />}
          showsVerticalScrollIndicator={false}>
          <ScrollView
            ref={category_strip_ref}
            horizontal
            showsHorizontalScrollIndicator={false}
            nestedScrollEnabled
            directionalLockEnabled
            decelerationRate="fast"
            snapToInterval={category_stride}
            snapToAlignment="start"
            disableIntervalMomentum
            bounces={false}
            contentContainerStyle={[
              styles.categoryStripContent,
              {
                paddingHorizontal: category_side_pad,
                paddingVertical: appTheme.spacing.sm,
              },
              horizontalLtrDirectionStyle,
            ]}
            style={[styles.categoryStrip, styles.categoryStripLtr]}
            onMomentumScrollEnd={on_category_momentum_end}>
            {categories.map((cat, category_index) => {
              const is_active = String(cat.id) === String(selected_category_id ?? selected_category?.id);
              const margin_end = category_index < categories.length - 1 ? CATEGORY_STRIP_GAP : 0;
              return (
                <CategoryStripItem
                  key={String(cat.id)}
                  cat={cat}
                  is_active={is_active}
                  card_width={category_banner_w}
                  card_height={category_banner_h}
                  margin_end={margin_end}
                  image_uri={category_image_uris.get(cat.id) ?? null}
                  styles={styles}
                  colors={c}
                  on_press={() => {
                    set_selected_category_id(cat.id);
                    set_selected_duration(null);
                    set_selected_plan(null);
                    scroll_category_to_index(category_index, true);
                  }}
                />
              );
            })}
          </ScrollView>

          <Text style={styles.sectionLabel}>{t('subscriptions.howManyMembers') || 'How many members?'}</Text>
          <View style={styles.chipRow}>
            {member_portion_options.map(n => {
              const is_active = portionsEqual(selected_portion, n);
              return (
                <Clickable
                  key={String(n)}
                  onPress={() => {
                    set_selected_portion(n);
                    set_selected_plan(null);
                  }}
                  pressAnimated={false}
                  contentStyle={styles.chipContentFill}
                  style={[styles.chip, styles.chipMember, is_active && styles.chipActive]}>
                  {is_active ? <LinearGradient colors={primary_gradient as [string, string]} style={styles.chipGradientFill} /> : null}
                  <Text style={[styles.chipText, is_active && styles.chipTextActive]}>{formatPersonCountLabel(n, t, portion_text_mode)}</Text>
                </Clickable>
              );
            })}
          </View>

          <Text style={styles.sectionLabel}>{t('subscriptions.durationOfPlan') || 'Duration Of Plan?'}</Text>
          <View style={styles.chipRow}>
            {durations.map(d => {
              const is_active = effective_duration === d;
              return (
                <Clickable
                  key={d}
                  onPress={() => set_selected_duration(d)}
                  pressAnimated={false}
                  contentStyle={styles.chipContentFill}
                  style={[styles.chip, styles.chipDuration, is_active && styles.chipActive]}>
                  {is_active ? <LinearGradient colors={primary_gradient as [string, string]} style={styles.chipGradientFill} /> : null}
                  <Text style={[styles.chipText, is_active && styles.chipTextActive]}>{d}</Text>
                </Clickable>
              );
            })}
          </View>

          <Text style={styles.sectionLabel}>{t('subscriptions.availablePlans') || 'Available Plans'}</Text>
          <View style={[styles.planListContainer, use_grid_layout && styles.planListGrid]}>
            {plan_items.map(render_plan_card)}
          </View>
        </ScrollView>

        <View style={[styles.bottomBarShell, { paddingBottom: Math.max(insets.bottom, appTheme.spacing.sm) }]} pointerEvents="box-none">
          <LinearGradient colors={primary_gradient as [string, string]} style={StyleSheet.absoluteFill} />
          <View style={styles.bottomBarHairline} pointerEvents="none" />
          <Pressable
            onPress={handle_proceed}
            disabled={!selected_plan}
            accessibilityRole="button"
            accessibilityLabel={t('subscriptions.proceedToCheckout')}
            accessibilityState={{ disabled: !selected_plan }}
            style={({ pressed }) => [styles.bottomBarPressable, !selected_plan && styles.bottomBarPressableDisabled, selected_plan && pressed && styles.bottomBarPressablePressed]}>
            <View style={styles.bottomBarRow}>
              <View style={styles.bottomBarTextBlock}>
                <Text style={styles.bottomBarPlanLine} numberOfLines={2} ellipsizeMode="tail">
                  {plan_summary_label}
                </Text>
                <Text style={styles.bottomBarTotalLine} numberOfLines={1}>
                  {formatSubscriptionFooterTotal(t, String(total_label))}
                </Text>
              </View>
              <View style={styles.bottomBarCtaBadge} pointerEvents="none">
                <SvgIcon name={trailingRowDisclosureChevron()} size={20} color={c.onPrimary ?? c.surface} />
              </View>
            </View>
          </Pressable>
        </View>

        <BottomSheetPopup
          visible={show_free_days_modal}
          onClose={() => {
            set_show_free_days_modal(false);
            set_selected_plan_with_free_days(null);
          }}
          title={t('subscriptions.freeDays')}
          animationType="slide"
          showDragHandle
          sheetStyle={{ paddingBottom: (insets.bottom || 0) + appTheme.spacing.md }}>
          <View style={styles.freeDaysSheetBody}>
            <Text style={styles.freeDaysMessage}>
              {t('subscriptions.addFreeDaysQuestion')}
              <Text style={styles.freeDaysHighlight}>
                {selected_plan_with_free_days?.free_days} {t('subscriptions.daysLabel')}
              </Text>
              {t('subscriptions.freeDaysSuffix')}
            </Text>
            <Clickable
              onPress={() => set_include_free_days(false)}
              style={[styles.freeDaysOption, !include_free_days && styles.freeDaysOptionActive, { borderColor: !include_free_days ? c.primary : c.borderLight, backgroundColor: !include_free_days ? hexToRgba(c.primary, 0.08) : c.surface }]}>
              <Text style={[styles.freeDaysOptionText, !include_free_days && styles.freeDaysOptionTextActive]}>{t('subscriptions.noThanks')}</Text>
            </Clickable>
            <Clickable
              onPress={() => set_include_free_days(true)}
              style={[styles.freeDaysOption, include_free_days && styles.freeDaysOptionActive, { borderColor: include_free_days ? c.primary : c.borderLight, backgroundColor: include_free_days ? hexToRgba(c.primary, 0.08) : c.surface }]}>
              <Text style={[styles.freeDaysOptionText, include_free_days && styles.freeDaysOptionTextActive]}>
                {t('subscriptions.yesFreeDays')}
                {selected_plan_with_free_days?.free_days}
                {t('subscriptions.yesFreeDaysSuffix')}
              </Text>
            </Clickable>
            <Clickable onPress={handle_confirm_free_days} style={styles.freeDaysConfirmBtn}>
              <Text style={styles.freeDaysConfirmText}>{t('subscriptions.continue')}</Text>
            </Clickable>
          </View>
        </BottomSheetPopup>
      </View>
    </StackScreenShell>
  );
};

export default SubscriptionListPremiumScreen;
